// @flow
import React, { Children, useState, type Node } from 'react';
import { Collapse, UnmountClosed } from 'react-collapse';
import classnames from 'classnames';

import { generateUniqueId } from 'Utils/unique-id/uniqueId';
import { ICONS } from 'Components/utils/icon/icons';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { Toggler } from 'Components/frameworks/toggler/Toggler.jsx';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { themes } from './CollapsePanel.themes';

const POSITIONS = {
  TOP: 'top',
  BOTTOM: 'bottom',
};

type CollapsePanelProps = {
  as?: string,
  children: Node,
  className?: string,
  collapseIconName?: IconName,
  collapseLabel?: string,
  expandIconName?: IconName,
  expandLabel?: string,
  hasKeepCollapsedContent?: boolean,
  iconName?: IconName,
  id?: string,
  isExpanded?: boolean,
  onClick?: () => void,
  theme?: string,
  title?: Node,
  trackingData?: ?TrackingData,
  loadMoreButtonPosition?: $Values<typeof POSITIONS>,
};

function hasLabels(props: CollapsePanelProps, isActive: boolean) {
  const { collapseIconName, collapseLabel, expandIconName, expandLabel } = props;
  return isActive ? collapseIconName || collapseLabel : expandIconName || expandLabel;
}

type LoadMoreButtonProps = CollapsePanelProps & {
  ariaId: string,
  isActive: boolean,
  toggle: (*) => void,
};

const LoadMoreButton = ({
  ariaId,
  isActive,
  toggle,
  ...collapsePanelProps
}: LoadMoreButtonProps) => {
  const {
    as: HeaderTag = 'div',
    collapseLabel,
    collapseIconName = ICONS.CROSS,
    expandLabel,
    expandIconName = ICONS.BOLDER_ARROW_FORTH_DOWN,
    iconName,
    isExpanded,
    theme = 'default',
    title,
    trackingData,
  } = collapsePanelProps;

  const {
    thButton,
    thHeader,
    thHeaderDecorator,
    thHeaderDecoratorActive,
    thLabels,
    thLabel,
    thLabelIcon,
    thLabelIconActive,
    thLabelIconWidth,
    thTitle,
    thTitleHasEllipsis,
    thTitleIcon,
    thTitleIconColor,
    thTitleIconSize,
  } = themes[theme];

  return (
    <HeaderTag className={classnames('reset-heading', thHeader)}>
      <TrackClick
        as="button"
        aria-controls={ariaId}
        aria-expanded={isActive}
        className={thButton}
        onClick={toggle}
        type="button"
        trackingData={isExpanded ? null : trackingData}
      >
        {!!title && (
          <div className={thTitle}>
            <Icon
              className={classnames('rtl:mr-2 flex-shrink-0', thTitleIcon)}
              color={thTitleIconColor}
              name={iconName}
              width={thTitleIconSize || '1em'}
            />
            {thTitleHasEllipsis ? <span className="truncate w-full">{title}</span> : title}
          </div>
        )}
        {hasLabels(collapsePanelProps, isActive) && (
          <div className={thLabels}>
            <span className={thLabel} data-testid="CollapsePanel-label">
              {isActive ? collapseLabel : expandLabel}
            </span>
            <Icon
              className={classnames(thLabelIcon, {
                [thLabelIconActive]: isActive,
              })}
              name={isActive ? collapseIconName : expandIconName}
              width={thLabelIconWidth}
            />
          </div>
        )}
      </TrackClick>
      {(thHeaderDecorator || thHeaderDecoratorActive) && (
        <div
          className={classnames(thHeaderDecorator, {
            [thHeaderDecoratorActive]: isActive,
          })}
        />
      )}
    </HeaderTag>
  );
};

export function CollapsePanel(props: CollapsePanelProps) {
  const [isVisible, setVisible] = useState(props.isExpanded);

  if (!Children.count(props.children)) {
    return null;
  }

  const {
    children,
    className,
    hasKeepCollapsedContent = true,
    id,
    isExpanded,
    onClick,
    theme = 'default',
    title,
    loadMoreButtonPosition = POSITIONS.TOP,
  } = props;

  const { thBody, thContent, thRoot } = themes[theme];

  const ariaId = generateUniqueId([id || title || ''], 'CollapsePanel_');
  const CollapsedTag = hasKeepCollapsedContent ? Collapse : UnmountClosed;

  return (
    <Toggler isActive={isExpanded} onToggle={onClick}>
      {(toggle, isActive) => (
        <section className={classnames(className, thRoot)} id={id}>
          {loadMoreButtonPosition === POSITIONS.TOP ? (
            <LoadMoreButton {...props} ariaId={ariaId} isActive={isActive} toggle={toggle} />
          ) : null}
          <div
            aria-hidden={!isActive}
            className={classnames(thBody, { invisible: !isVisible })}
            id={ariaId}
          >
            <CollapsedTag
              isOpened={isActive}
              onWork={() => {
                setVisible(true);
              }}
              onRest={({ isFullyClosed }) => {
                setVisible(!isFullyClosed);
              }}
            >
              <div className={thContent}>{children}</div>
            </CollapsedTag>
          </div>
          {loadMoreButtonPosition === POSITIONS.BOTTOM ? (
            <LoadMoreButton {...props} ariaId={ariaId} isActive={isActive} toggle={toggle} />
          ) : null}
        </section>
      )}
    </Toggler>
  );
}
